export const normalizeEsewaTransactionUuid = (value) =>
  String(value || "").trim();

export const getEsewaSubmissionStorageKey = (transactionUuid) => {
  const normalizedUuid = normalizeEsewaTransactionUuid(
    transactionUuid,
  );

  return normalizedUuid
    ? `onground:esewa-submitted:${normalizedUuid}`
    : "";
};

export const wasEsewaTransactionSubmitted = (
  storage,
  transactionUuid,
) => {
  const key = getEsewaSubmissionStorageKey(transactionUuid);

  if (!storage || !key) return false;

  try {
    return storage.getItem(key) === "1";
  } catch {
    return false;
  }
};

export const markEsewaTransactionSubmitted = (
  storage,
  transactionUuid,
) => {
  const key = getEsewaSubmissionStorageKey(transactionUuid);

  if (!storage || !key) return false;

  try {
    storage.setItem(key, "1");
    return true;
  } catch {
    return false;
  }
};

export const clearEsewaTransactionSubmitted = (
  storage,
  transactionUuid,
) => {
  const key = getEsewaSubmissionStorageKey(transactionUuid);

  if (!storage || !key) return false;

  try {
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

const ESEWA_RECOVERY_STORAGE_KEY =
  "onground:esewa-recovery";

const normalizeBookingSnapshot = (value = {}) => {
  const customer = {
    userName: String(
      value?.customer?.userName || "",
    ),
    contactNumber: String(
      value?.customer?.contactNumber || "",
    ),
    email: String(
      value?.customer?.email || "",
    ),
  };

  const selectedItems = Array.isArray(
    value?.selectedItems,
  )
    ? value.selectedItems
        .slice(0, 24)
        .map((item) => ({
          date: String(item?.date || ""),
          start: String(item?.start || ""),
          end: String(item?.end || ""),
          price:
            item?.price == null
              ? null
              : Number(item.price),
          discountedPrice:
            item?.discountedPrice == null
              ? null
              : Number(item.discountedPrice),
          currency: String(
            item?.currency || "",
          ),
        }))
        .filter(
          (item) =>
            item.date &&
            item.start &&
            item.end,
        )
    : [];

  const rawPromoPreview =
    value?.promoPreview &&
    typeof value.promoPreview === "object"
      ? value.promoPreview
      : null;

  const promoPreview = rawPromoPreview
    ? {
        code: String(
          rawPromoPreview.code || "",
        ),
        description: String(
          rawPromoPreview.description || "",
        ),
        subtotal: Number(
          rawPromoPreview.subtotal || 0,
        ),
        discountAmount: Number(
          rawPromoPreview.discountAmount || 0,
        ),
        finalAmount: Number(
          rawPromoPreview.finalAmount || 0,
        ),
      }
    : null;

  return {
    customer,
    phoneCountryCode: String(
      value?.phoneCountryCode || "",
    ),
    selectedGroundId: String(
      value?.selectedGroundId || "",
    ),
    selectedDate: String(
      value?.selectedDate || "",
    ),
    selectedItems,
    promoCode: String(
      value?.promoCode || "",
    ),
    promoPreview,
  };
};

const normalizeRecoveryContext = (value = {}) => {
  const slug = String(value?.slug || "").trim();
  const reservationId = String(
    value?.reservationId || "",
  ).trim();
  const releaseToken = String(
    value?.releaseToken || "",
  ).trim();
  const transactionUuid =
    normalizeEsewaTransactionUuid(
      value?.transactionUuid,
    );
  const expiresAt = String(
    value?.expiresAt || "",
  ).trim();

  if (
    !slug ||
    !reservationId ||
    !releaseToken
  ) {
    return null;
  }

  const startedAt = Number(
    value?.startedAt || Date.now(),
  );

  return {
    slug,
    reservationId,
    releaseToken,
    transactionUuid,
    expiresAt,
    startedAt: Number.isFinite(startedAt)
      ? startedAt
      : Date.now(),
    bookingSnapshot:
      normalizeBookingSnapshot(
        value?.bookingSnapshot,
      ),
  };
};

export const saveEsewaRecoveryContext = (
  storage,
  value,
) => {
  if (!storage) return false;

  const normalized =
    normalizeRecoveryContext(value);

  if (!normalized) return false;

  try {
    storage.setItem(
      ESEWA_RECOVERY_STORAGE_KEY,
      JSON.stringify(normalized),
    );
    return true;
  } catch {
    return false;
  }
};

export const getEsewaRecoveryContext = (
  storage,
) => {
  if (!storage) return null;

  try {
    const raw = storage.getItem(
      ESEWA_RECOVERY_STORAGE_KEY,
    );

    if (!raw) return null;

    return normalizeRecoveryContext(
      JSON.parse(raw),
    );
  } catch {
    return null;
  }
};

export const clearEsewaRecoveryContext = (
  storage,
) => {
  if (!storage) return false;

  try {
    storage.removeItem(
      ESEWA_RECOVERY_STORAGE_KEY,
    );
    return true;
  } catch {
    return false;
  }
};
